// Builds and searches the home page's index of everything the console manages.
//
// Every list is already fetched whole elsewhere in the app under these same query keys, so
// the index is built client side from the cache rather than from a search endpoint.

// route is where a hit sends you; the page opens that row's edit dialog from ?id=.
const TYPES = [
  {
    type: 'owner',
    label: 'Owners',
    route: '/owners',
    // Fields worth matching on, beyond name and id. Kept short on purpose: matching every
    // column turns a search for "milk" into a list of everything a farm ever touched.
    fields: ['description', 'timezone', 'business_id'],
  },
  {
    type: 'provider',
    label: 'Providers',
    route: '/providers',
    fields: ['description', 'partition_scheme'],
  },
  {
    type: 'data_stream',
    label: 'Data Streams',
    route: '/data-streams',
    fields: ['description', 'owner_name', 'provider_name'],
  },
  {
    type: 'consumer',
    label: 'Consumers',
    route: '/consumers',
    fields: ['description', 'consumer_type_name'],
  },
];

// What each hit shows under its name, so two streams called "Milk Weights" can be told
// apart without opening them.
const SUBTITLES = {
  owner: (row) => [row.timezone, row.description].filter(Boolean).join(' · '),
  provider: (row) => [row.partition_scheme, row.description].filter(Boolean).join(' · '),
  data_stream: (row) => [row.owner_name, row.provider_name].filter(Boolean).join(' · '),
  consumer: (row) => [row.consumer_type_name, row.description].filter(Boolean).join(' · '),
};

const text = (value) => (value === null || value === undefined ? '' : String(value));

const buildIndex = (rowsByType) =>
  TYPES.flatMap(({type, label, route, fields}) =>
    (rowsByType[type] || []).map((row) => ({
      type,
      typeLabel: label,
      route,
      id: row.id,
      name: text(row.name) || `(unnamed ${type})`,
      subtitle: SUBTITLES[type](row),
      disabled: Boolean(row.disabled),
      // Lowercased once at build time; searching re-lowercases nothing per keystroke.
      haystack: [row.name, ...fields.map((field) => row[field])]
        .map(text)
        .join(' ')
        .toLowerCase(),
    }))
  );

// Rank so the thing you typed the start of comes first: an exact id, then a name that
// starts with the query, then a name containing it, then a match anywhere else.
const rank = (entry, query) => {
  if (String(entry.id) === query) return 0;
  const name = entry.name.toLowerCase();
  if (name.startsWith(query)) return 1;
  if (name.includes(query)) return 2;
  return entry.haystack.includes(query) ? 3 : -1;
};

const search = (index, rawQuery, limit = 25) => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return [];

  const scored = [];
  for (const entry of index) {
    const score = rank(entry, query);
    if (score >= 0) scored.push({entry, score});
  }

  scored.sort((a, b) =>
    a.score - b.score
    || a.entry.name.localeCompare(b.entry.name)
    || Number(a.entry.id) - Number(b.entry.id));

  return scored.slice(0, limit).map((hit) => hit.entry);
};

// Where selecting a hit goes. The target page reads ?id= and opens that row.
const linkTo = (entry) => `${entry.route}?id=${entry.id}`;

export default {TYPES, buildIndex, search, linkTo};
